package com.tovtalk.ui.screen.upload

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.VolumeOff
import androidx.compose.material.icons.automirrored.filled.VolumeUp
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.VideoLibrary
import androidx.compose.material.icons.filled.VolumeOff
import androidx.compose.material.icons.filled.VolumeUp
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.tovtalk.di.koinInject
import com.tovtalk.domain.repository.HashtagInfo
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.upload.UploadViewModel
import com.tovtalk.ui.components.UploadPreviewVideoPlayer
import com.tovtalk.ui.platform.rememberLocalVideoPreviewUri
import com.tovtalk.ui.components.CenteredContentWrapper
import com.tovtalk.ui.platform.rememberVideoPicker
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringProvider

private const val MAX_FILE_SIZE_MB = 100
private const val MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun UploadScreen(
    onShowError: (String) -> Unit = {},
    onUploadSuccess: (message: String, userId: Int, postId: Int) -> Unit = { _, _, _ -> },
    viewModel: UploadViewModel = koinInject()
) {
    // State collection
    val uploadState by viewModel.uploadState.collectAsState()
    val uploadProgress by viewModel.uploadProgress.collectAsState()
    val lastUploadedPost by viewModel.lastUploadedPost.collectAsState()
    val isPermanent by viewModel.isPermanent.collectAsState()
    val trendingHashtags by viewModel.trendingHashtags.collectAsState()
    val hashtagSuggestions by viewModel.hashtagSuggestions.collectAsState()

    var description by remember { mutableStateOf("") }
    var selectedBytes by remember { mutableStateOf<ByteArray?>(null) }
    var selectedFileName by remember { mutableStateOf<String?>(null) }
    var isMuted by remember { mutableStateOf(true) }
    var isPlaying by remember { mutableStateOf(true) }
    var fileSizeError by remember { mutableStateOf<String?>(null) }
    val snackbarHostState = remember { SnackbarHostState() }

    val animatedProgress by animateFloatAsState(
        targetValue = uploadProgress,
        label = "uploadProgress"
    )

    val pickVideo = rememberVideoPicker { bytes, filename ->
        if (bytes != null) {
            if (bytes.size > MAX_FILE_SIZE_BYTES) {
                val fileSizeMB = bytes.size / (1024f * 1024f)
                fileSizeError = StringProvider.get(
                    "upload_error_file_too_large",
                    fileSizeMB.toString(),
                    MAX_FILE_SIZE_MB.toString()
                )
                viewModel.resetState()
                selectedBytes = null
                selectedFileName = null
            } else {
                viewModel.resetState()
                selectedBytes = bytes
                selectedFileName = filename
                    ?: "upload_${com.tovtalk.utils.nowInstant().toEpochMilliseconds()}.mp4"
                fileSizeError = null
            }
        }
    }

    // Show file size error as snackbar
    LaunchedEffect(fileSizeError) {
        fileSizeError?.let { error ->
            snackbarHostState.showSnackbar(error)
        }
    }

    // Handle upload success
    LaunchedEffect(uploadState, lastUploadedPost) {
        if (uploadState is UiState.Success && lastUploadedPost != null) {
            val post = lastUploadedPost!!
            val message = StringProvider.get("upload_success_snackbar")
            onUploadSuccess(message, post.author.id, post.id)
            viewModel.resetState()
            selectedBytes = null
            selectedFileName = null
            description = ""
        }
    }

    // Handle upload error
    LaunchedEffect(uploadState) {
        if (uploadState is UiState.Error) {
            snackbarHostState.showSnackbar((uploadState as UiState.Error).message)
        }
    }

    val uploadContent = @Composable {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(TovTalkTheme.colors.backgroundPrimary)
    ) {
        if (selectedBytes == null) {
            // === INITIAL STATE: No video selected ===
            InitialVideoSelection(
                onSelectClick = { pickVideo() },
                selectVideoText = StringProvider.get("upload_select_video"),
                maxSizeText = StringProvider.get("upload_max_size", MAX_FILE_SIZE_MB.toString())
            )
        } else {
            // === MAIN STATE: Video selected, TikTok-style ===
            val previewUri = rememberLocalVideoPreviewUri(selectedBytes, selectedFileName)

            // Layer 1: Video background
            UploadPreviewVideoPlayer(
                videoUrl = previewUri,
                modifier = Modifier.fillMaxSize(),
                compactHeight = 0.dp, // ignored when fillContainer=true
                loop = true,
                muted = isMuted,
                isPlaying = isPlaying,
                fillContainer = true,
                onTap = { isPlaying = !isPlaying }
            )

            // Layer 2: Top bar with gradient
            TopBarOverlay(
                onClose = {
                    viewModel.resetState()
                    selectedBytes = null
                    selectedFileName = null
                    description = ""
                },
                isMuted = isMuted,
                onToggleMute = { isMuted = !isMuted }
            )

            // Layer 3: Bottom overlay with controls
            BottomControlsOverlay(
                description = description,
                onDescriptionChange = { newValue ->
                    description = newValue
                    // Detect # typing for autocomplete
                    val hashIndex = newValue.lastIndexOf('#')
                    if (hashIndex >= 0) {
                        val query =
                            newValue.substring(hashIndex + 1).takeWhile { !it.isWhitespace() }
                        if (query.isNotEmpty()) {
                            viewModel.searchHashtags(query)
                        } else {
                            viewModel.clearHashtagSuggestions()
                        }
                    } else {
                        viewModel.clearHashtagSuggestions()
                    }
                },
                hashtagSuggestions = hashtagSuggestions,
                onHashtagSelected = { hashtag ->
                    // Replace the partial #query with the full hashtag
                    val hashIndex = description.lastIndexOf('#')
                    if (hashIndex >= 0) {
                        val beforeHash = description.substring(0, hashIndex)
                        description = "$beforeHash#$hashtag "
                    } else {
                        description = "$description #$hashtag "
                    }
                    viewModel.clearHashtagSuggestions()
                },
                trendingHashtags = trendingHashtags,
                onTrendingHashtagClick = { hashtag ->
                    description =
                        if (description.isBlank()) "#$hashtag " else "$description #$hashtag "
                },
                isPermanent = isPermanent,
                onToggle24h = { viewModel.setIsPermanent(!isPermanent) },
                uploadState = uploadState,
                animatedProgress = animatedProgress,
                onPublish = {
                    selectedBytes?.let { bytes ->
                        viewModel.uploadReel(
                            bytes = bytes,
                            filename = selectedFileName ?: "upload.mp4",
                            description = description,
                            isPermanent = isPermanent,
                            category = "posts"
                        )
                    }
                },
                isPublishEnabled = selectedBytes != null && uploadState !is UiState.Loading
            )
        }

        // Snackbar host for errors
        SnackbarHost(
            hostState = snackbarHostState,
            modifier = Modifier
                .align(Alignment.TopCenter)
                .statusBarsPadding()
        )
    }
    }
    CenteredContentWrapper { uploadContent() }
}

@Composable
private fun InitialVideoSelection(
    onSelectClick: () -> Unit,
    selectVideoText: String,
    maxSizeText: String
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .clickable(onClick = onSelectClick),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Icon(
                Icons.Default.VideoLibrary,
                contentDescription = null,
                modifier = Modifier.size(64.dp),
                tint = TovTalkTheme.colors.textTertiary
            )
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
            Text(
                text = selectVideoText,
                style = TovTalkTheme.typography.titleMedium,
                color = TovTalkTheme.colors.textPrimary
            )
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xs))
            Text(
                text = maxSizeText,
                style = TovTalkTheme.typography.bodySmall,
                color = TovTalkTheme.colors.textTertiary
            )
        }
    }
}

@Composable
private fun TopBarOverlay(
    onClose: () -> Unit,
    isMuted: Boolean,
    onToggleMute: () -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight(0.15f)
            .background(
                Brush.verticalGradient(
                    colors = listOf(
                        TovTalkTheme.colors.videoOverlayGradientTop,
                        Color.Transparent
                    )
                )
            )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .statusBarsPadding()
                .padding(
                    horizontal = TovTalkTheme.spacing.md,
                    vertical = TovTalkTheme.spacing.sm
                ),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            IconButton(onClick = onClose) {
                Icon(
                    Icons.Default.Close,
                    contentDescription = null,
                    tint = TovTalkTheme.colors.onBrand
                )
            }
            IconButton(onClick = onToggleMute) {
                Icon(
                    if (isMuted) Icons.AutoMirrored.Filled.VolumeOff else Icons.AutoMirrored.Filled.VolumeUp,
                    contentDescription = null,
                    tint = TovTalkTheme.colors.onBrand
                )
            }
        }
    }
}

@Composable
private fun BottomControlsOverlay(
    description: String,
    onDescriptionChange: (String) -> Unit,
    hashtagSuggestions: List<HashtagInfo>,
    onHashtagSelected: (String) -> Unit,
    trendingHashtags: List<HashtagInfo>,
    onTrendingHashtagClick: (String) -> Unit,
    isPermanent: Boolean,
    onToggle24h: () -> Unit,
    uploadState: UiState<String>,
    animatedProgress: Float,
    onPublish: () -> Unit,
    isPublishEnabled: Boolean
) {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.BottomCenter
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight(0.45f)
                .background(
                    Brush.verticalGradient(
                        colors = listOf(
                            Color.Transparent,
                            TovTalkTheme.colors.videoOverlayGradientBottom,
                            TovTalkTheme.colors.backgroundPrimary.copy(alpha = TovTalkTheme.opacity.intense)
                        )
                    )
                )
                .padding(horizontal = TovTalkTheme.spacing.md)
                .navigationBarsPadding()
                .imePadding(),
            verticalArrangement = Arrangement.Bottom
        ) {
            // Hashtag autocomplete dropdown (shown above description)
            if (hashtagSuggestions.isNotEmpty()) {
                HashtagAutocompleteDropdown(
                    suggestions = hashtagSuggestions,
                    onSelect = onHashtagSelected
                )
                Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xs))
            }

            // Description field
            BasicTextField(
                value = description,
                onValueChange = onDescriptionChange,
                modifier = Modifier
                    .fillMaxWidth()
                    .background(
                        TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.gentle),
                        RoundedCornerShape(TovTalkTheme.radius.sm)
                    )
                    .padding(TovTalkTheme.spacing.sm),
                textStyle = TovTalkTheme.typography.bodyMedium.copy(color = TovTalkTheme.colors.onBrand),
                maxLines = 3,
                decorationBox = { innerTextField ->
                    Box {
                        if (description.isEmpty()) {
                            Text(
                                text = StringProvider.get("upload_add_description"),
                                style = TovTalkTheme.typography.bodyMedium,
                                color = TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.half)
                            )
                        }
                        innerTextField()
                    }
                }
            )

            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))

            // Trending hashtags row
            if (trendingHashtags.isNotEmpty()) {
                TrendingHashtagsRow(
                    hashtags = trendingHashtags,
                    onHashtagClick = onTrendingHashtagClick
                )
                Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
            }

            // 24h toggle
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = StringProvider.get("upload_story_toggle_label"),
                    style = TovTalkTheme.typography.bodySmall,
                    color = TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.heavy)
                )
                Switch(
                    checked = !isPermanent,
                    onCheckedChange = { onToggle24h() },
                    colors = SwitchDefaults.colors(
                        checkedTrackColor = TovTalkTheme.colors.brand,
                        uncheckedTrackColor = TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.moderate)
                    )
                )
            }

            // 24h info text
            AnimatedVisibility(visible = !isPermanent) {
                Text(
                    text = StringProvider.get("upload_story_info"),
                    style = TovTalkTheme.typography.labelSmall,
                    color = TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.half),
                    modifier = Modifier.padding(bottom = TovTalkTheme.spacing.xs)
                )
            }

            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))

            // Post button with progress
            ProgressPostButton(
                isPermanent = isPermanent,
                isLoading = uploadState is UiState.Loading,
                progress = animatedProgress,
                enabled = isPublishEnabled,
                onClick = onPublish
            )

            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
        }
    }
}

@Composable
private fun TrendingHashtagsRow(
    hashtags: List<HashtagInfo>,
    onHashtagClick: (String) -> Unit
) {
    LazyRow(
        horizontalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.xs),
        contentPadding = PaddingValues(horizontal = 0.dp)
    ) {
        items(hashtags) { hashtag ->
            Text(
                text = "#${hashtag.hashtag}",
                style = TovTalkTheme.typography.labelSmall,
                color = TovTalkTheme.colors.onBrand,
                modifier = Modifier
                    .background(
                        TovTalkTheme.colors.onBrand.copy(alpha = TovTalkTheme.opacity.mild),
                        RoundedCornerShape(TovTalkTheme.radius.full)
                    )
                    .clickable { onHashtagClick(hashtag.hashtag) }
                    .padding(
                        horizontal = TovTalkTheme.spacing.sm,
                        vertical = TovTalkTheme.spacing.xxs
                    )
            )
        }
    }
}

@Composable
private fun HashtagAutocompleteDropdown(
    suggestions: List<HashtagInfo>,
    onSelect: (String) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                TovTalkTheme.colors.surfaceElevated,
                RoundedCornerShape(TovTalkTheme.radius.sm)
            )
    ) {
        suggestions.forEach { hashtag ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable { onSelect(hashtag.hashtag) }
                    .padding(
                        horizontal = TovTalkTheme.spacing.sm,
                        vertical = TovTalkTheme.spacing.xs
                    ),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "#${hashtag.hashtag}",
                    style = TovTalkTheme.typography.bodyMedium,
                    color = TovTalkTheme.colors.textPrimary
                )
                Text(
                    text = "${hashtag.postCount}",
                    style = TovTalkTheme.typography.labelSmall,
                    color = TovTalkTheme.colors.textTertiary
                )
            }
        }
    }
}

@Composable
private fun ProgressPostButton(
    isPermanent: Boolean,
    isLoading: Boolean,
    progress: Float,
    enabled: Boolean,
    onClick: () -> Unit
) {
    val buttonText = when {
        isLoading -> StringProvider.get(
            "upload_publishing_progress",
            (progress * 100).toInt().toString()
        )
        isPermanent -> StringProvider.get("upload_publish")
        else -> StringProvider.get("upload_publish_24h")
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(TovTalkTheme.sizes.buttonHeightMd)
            .clip(RoundedCornerShape(TovTalkTheme.radius.md))
            .background(
                if (enabled || isLoading) TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.subtle)
                else TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.mild)
            )
            .then(
                if (enabled && !isLoading) Modifier.clickable(onClick = onClick)
                else Modifier
            ),
        contentAlignment = Alignment.CenterStart
    ) {
        // Progress fill
        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .fillMaxWidth(progress)
                    .background(TovTalkTheme.colors.brand)
            )
        } else if (enabled) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(TovTalkTheme.colors.brand)
            )
        }

        // Button text (centered)
        Text(
            text = buttonText,
            style = TovTalkTheme.typography.titleSmall,
            color = TovTalkTheme.colors.onBrand,
            modifier = Modifier.align(Alignment.Center)
        )
    }
}
